import React, { useEffect, useRef, useState } from "react";
import { FaBrain } from "react-icons/fa6";
import { FiMessageCircle, FiPlus, FiSmile } from "react-icons/fi";
import SteffiDevice from "../components/SteffiDevice";
import Reveal from "../components/Reveal";
import { useSteffiCopy } from "../content";
import { RENDERS } from "../renders";

const ICONS = { ease: FiSmile, memory: FaBrain, ask: FiMessageCircle };

/* Steffi's size beside the panels, as a share of the full heading-to-last-
   panel span */
const BOT_SCALE = 0.85;

/* layout position of `el` inside `ancestor`, ignoring transforms (the reveal
   animation slides the copy in, which must not move Steffi) */
const topWithin = (el, ancestor) => {
  let y = 0;
  for (let n = el; n && n !== ancestor; n = n.offsetParent) y += n.offsetTop;
  return y;
};

function Panel({ card, index, open, onOpen }) {
  const Icon = ICONS[card.icon] || FiSmile;

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(index)}
        aria-expanded={open}
        aria-controls={`voice-panel-${index}`}
        className="group w-full text-left"
      >
        <div className={`card voice-tab overflow-hidden px-7 py-[clamp(0.9rem,2.6svh,1.6rem)] sm:px-8 ${open ? "is-open" : ""}`}>
          <div className="flex items-center gap-4">
            <span
              aria-hidden
              className="grid h-14 w-14 shrink-0 place-items-center rounded-full transition-all duration-700"
              style={{
                transitionTimingFunction: "cubic-bezier(.2,.8,.2,1)",
                background: "var(--bg-2)",
                color: "var(--color-highlight)",
              }}
            >
              <Icon className="h-6 w-6" />
            </span>

            <span className="min-w-0 flex-1">
              <span className="t-feature block !text-[clamp(1.1875rem,1.55vw,1.8rem)] leading-snug">
                {card.title}
              </span>
              <span className="mt-1.5 block text-[clamp(0.9375rem,1.15vw,1.3rem)] leading-snug text-[color:var(--ink-2)]">
                {card.body}
              </span>
            </span>

            <span
              aria-hidden
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full border transition-all duration-700 group-hover:border-[color:var(--ink-3)]"
              style={{
                transitionTimingFunction: "cubic-bezier(.2,.8,.2,1)",
                borderColor: "var(--border)",
                color: open ? "var(--ink-2)" : "var(--ink-3)",
                transform: `rotate(${open ? 135 : 0}deg)`,
              }}
            >
              <FiPlus className="h-[1.15rem] w-[1.15rem]" />
            </span>
          </div>

          {/* 0fr → 1fr animates the height smoothly with no measuring */}
          <div
            id={`voice-panel-${index}`}
            role="region"
            className="grid transition-[grid-template-rows] duration-700"
            style={{
              gridTemplateRows: open ? "1fr" : "0fr",
              transitionTimingFunction: "cubic-bezier(.2,.8,.2,1)",
            }}
          >
            <div className="overflow-hidden">
              <p
                className="pl-[4.5rem] pr-2 pt-4 text-[clamp(1rem,1.2vw,1.35rem)] leading-relaxed text-[color:var(--ink-2)] transition-all duration-700"
                style={{
                  transitionTimingFunction: "cubic-bezier(.2,.8,.2,1)",
                  opacity: open ? 1 : 0,
                  transform: `translateY(${open ? 0 : 8}px)`,
                }}
              >
                {card.detail}
              </p>
            </div>
          </div>
        </div>
      </button>
    </li>
  );
}

export default function VoiceSection() {
  const { VOICE } = useSteffiCopy();
  const [open, setOpen] = useState(0);

  /* Steffi is centred on the copy, from the top of the heading to the bottom
     of the last panel, at BOT_SCALE of that height. Follows a panel opening
     and the window changing size. */
  const stageRef = useRef(null);
  const copyRef = useRef(null);
  const panelsRef = useRef(null);
  const botRef = useRef(null);
  useEffect(() => {
    const stage = stageRef.current;
    const copy = copyRef.current;
    const panels = panelsRef.current;
    const bot = botRef.current;
    if (!stage || !copy || !panels || !bot) return undefined;
    const r = RENDERS.front;
    const headShare = 25 / 1860; // empty margin above her head in the render
    const baseShare = r.ground.y / 100; // where her base ends
    const align = () => {
      const heading = copy.querySelector("h2");
      if (!heading || !bot.offsetParent) return;
      const top = topWithin(heading, stage);
      const bottom = topWithin(panels, stage) + panels.offsetHeight;
      const imgH = (bottom - top) / (baseShare - headShare);
      const maxW = bot.offsetParent.clientWidth;
      const full = Math.min(maxW, (imgH * r.w) / r.h);
      const fullH = (full * r.h) / r.w;
      // a little smaller than the full span, still centred on it
      const w = full * BOT_SCALE;
      const h = fullH * BOT_SCALE;
      const middle = top - headShare * fullH + fullH / 2;
      const holder = topWithin(bot.offsetParent, stage);
      bot.style.width = `${w.toFixed(1)}px`;
      bot.style.top = `${(middle - h / 2 - holder).toFixed(1)}px`;
    };
    align();
    const ro = new ResizeObserver(align);
    ro.observe(stage);
    ro.observe(panels);
    return () => ro.disconnect();
  }, []);
  const [glance, setGlance] = useState({ token: 0, dir: { x: 0.8, y: 0 } });

  /* she looks towards whichever panel was just opened */
  const onOpen = (index) => {
    setOpen(index);
    setGlance((g) => ({
      token: g.token + 1,
      dir: { x: 0.85, y: -0.35 + (index / Math.max(1, VOICE.cards.length - 1)) * 0.7 },
    }));
  };

  return (
    <section
      id="sprechen"
      className="relative lg:h-[100svh]"
      style={{ background: "var(--bg)" }}
    >
      {/* The product is desktop-only: on a phone the device has already been
          introduced above, so this section keeps its focus on the heading and
          expandable panels. Steffi stands in place, she no longer rises in. */}
      <div
        ref={stageRef}
        className="lg:sticky lg:top-0 lg:h-[100svh] lg:overflow-hidden"
        style={{
          background:
            "var(--bg)",
        }}
      >
        {/* The product, level with the panels on the right. */}
        <div className="pointer-events-none relative hidden h-[50svh] overflow-hidden lg:absolute lg:inset-x-0 lg:bottom-0 lg:right-[54%] lg:block lg:h-full">
          <div className="absolute inset-0">
            {/* The render is 1.42× as tall as it is wide, so the width is capped
                against the viewport height as well as the column, keeping the
                whole product on screen. */}
            <div ref={botRef} className="voice-bot">
              <SteffiDevice
                variant="front"
                glanceToken={glance.token}
                glanceDir={glance.dir}
                alt={VOICE.alt}
              />
            </div>
          </div>
        </div>

        {/* spans the whole stage but only the text column takes the pointer, so
            Steffi underneath can still be stroked */}
        <div className="pointer-events-none relative z-10 mx-auto flex w-full max-w-[1440px] items-center px-6 pb-24 pt-14 lg:h-full lg:px-10 lg:pb-0 lg:pt-0">
          <div ref={copyRef} className="pointer-events-auto ml-auto w-full lg:w-[46%]">
            <Reveal as="p" className="t-eyebrow">
              {VOICE.eyebrow}
            </Reveal>
            <Reveal as="h2" delay={80} className="t-section mt-4 !text-[clamp(1.75rem,min(4.4vw,6.6svh),4.5rem)]">
              {VOICE.title.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </Reveal>
            <Reveal as="p" delay={160} className="mt-5 text-[clamp(1.25rem,1.5vw,1.55rem)] leading-snug text-[color:var(--ink-2)]">
              {VOICE.subtitle}
            </Reveal>

            <Reveal delay={220}>
              <ul ref={panelsRef} className="mt-7 flex flex-col gap-2.5">
                {VOICE.cards.map((card, i) => (
                  <Panel
                    key={card.title}
                    card={card}
                    index={i}
                    open={open === i}
                    onOpen={onOpen}
                  />
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
